import React from 'react';
import { Variable, CorrelationResult } from '../api/client';

interface Props {
  dependentVariables: Variable[];
  independentVariables: Variable[];
  correlations?: Record<string, Record<string, CorrelationResult>>;
}

const CorrelationView: React.FC<Props> = ({ dependentVariables, independentVariables, correlations }) => {
  if (!correlations || Object.keys(correlations).length === 0) {
    return (
      <div className="correlation-view">
        <h3>Correlation</h3>
        <p className="empty-state">
          No correlations computed yet. Add more data points and correlations will be calculated automatically.
        </p>
      </div>
    );
  }

  return (
    <div className="correlation-view">
      <h3>Correlation Matrix</h3>
      <p className="info">
        Correlations are computed automatically when data points are added or updated.
      </p>

      {dependentVariables.map((depVar) => {
        const row = correlations[depVar.id];
        if (!row) return null;

        return (
          <div key={depVar.id} className="correlation-section">
            <h4>{depVar.name}</h4>
            {independentVariables.map((indepVar) => {
              const result = row[indepVar.id];
              if (!result) return null;

              return (
                <div key={indepVar.id} className="correlation-result">
                  <div className="result-card">
                    <div className="result-header">
                      <span className="variable">{depVar.name}</span>
                      <span className="vs">vs</span>
                      <span className="variable">{indepVar.name}</span>
                    </div>

                    {result.error ? (
                      <div className="result-stats">
                        <div className="stat">
                          <label>Status</label>
                          <div className="value error-text">{result.error}</div>
                        </div>
                        <div className="stat">
                          <label>Sample Size</label>
                          <div className="value">{result.sample_size} data points</div>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="result-stats">
                          <div className="stat">
                            <label>Effect Size</label>
                            <div className={`correlation-value ${getCorrelationClass(result.effect_size)}`}>
                              {result.effect_size.toFixed(3)}
                            </div>
                          </div>

                          <div className="stat">
                            <label>p-value</label>
                            <div className="value">{result.p_value < 0.001 ? '< 0.001' : result.p_value.toFixed(3)}</div>
                          </div>

                          <div className="stat">
                            <label>Sample Size</label>
                            <div className="value">{result.sample_size} data points</div>
                          </div>

                          <div className="stat">
                            <label>Method</label>
                            <div className="value">{result.method}</div>
                          </div>
                        </div>

                        <div className="interpretation">
                          <h5>Interpretation</h5>
                          <p>{getInterpretation(result.effect_size)}</p>
                          {result.p_value < 0.05 ? (
                            <p className="significance significant">Statistically significant (p &lt; 0.05)</p>
                          ) : (
                            <p className="significance not-significant">Not statistically significant (p &ge; 0.05)</p>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

function getCorrelationClass(effectSize: number): string {
  if (effectSize >= 0.7) return 'strong';
  if (effectSize >= 0.4) return 'moderate';
  if (effectSize >= 0.1) return 'weak';
  return 'negligible';
}

function getInterpretation(effectSize: number): string {
  if (effectSize < 0.1) {
    return 'There appears to be negligible or no association between these variables.';
  } else if (effectSize < 0.4) {
    return 'There is a weak association. The variables show some relationship, but it\'s not very strong.';
  } else if (effectSize < 0.7) {
    return 'There is a moderate association. The variables show a meaningful relationship.';
  } else {
    return 'There is a strong association! The variables are highly related.';
  }
}

export default CorrelationView;
